import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Instagram, Phone, Send, CheckCircle } from 'lucide-react';
import { agencyInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('Form submitted:', formData);
    setSubmitted(true);
    
    toast({
      title: "Message sent successfully!",
      description: "We'll get back to you within 24 hours.",
    });

    setTimeout(() => {
      setFormData({ name: '', email: '', company: '', message: '' });
      setSubmitted(false);
    }, 3000);
  };

  return (
    <section id="contact" className="py-32 bg-white relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="mb-20">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-black">
            Let's Work Together
          </h2>
          <p className="text-xl text-[#666666] max-w-2xl font-light">
            Ready to scale your marketing with AI? Get in touch.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-16">
          {/* Contact form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-white border-black/20 text-black placeholder:text-[#666666] focus:border-black transition-colors h-12"
                  placeholder="Your name"
                />
              </div>

              <div>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white border-black/20 text-black placeholder:text-[#666666] focus:border-black transition-colors h-12"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="bg-white border-black/20 text-black placeholder:text-[#666666] focus:border-black transition-colors h-12"
                  placeholder="Company name"
                />
              </div>

              <div>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-white border-black/20 text-black placeholder:text-[#666666] focus:border-black transition-colors resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              <Button
                type="submit"
                disabled={submitted}
                className="w-full bg-black hover:bg-[#1a1a1a] text-white py-6 text-lg font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitted ? (
                  <span className="flex items-center gap-2 justify-center">
                    <CheckCircle className="w-5 h-5" />
                    Sent Successfully!
                  </span>
                ) : (
                  <span className="flex items-center gap-2 justify-center">
                    <Send className="w-5 h-5" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* Contact info */}
          <div className="space-y-8">
            {/* Contact methods */}
            <div className="space-y-6">
              <a
                href={`mailto:${agencyInfo.email}`}
                className="flex items-center gap-4 p-6 border border-black/10 transition-all duration-300 hover:border-black hover:shadow-lg group"
              >
                <div className="w-12 h-12 bg-black flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-[#666666] uppercase tracking-wider mb-1">Email</div>
                  <div className="text-black font-medium">{agencyInfo.email}</div>
                </div>
              </a>

              <a
                href={`https://instagram.com/${agencyInfo.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-6 border border-black/10 transition-all duration-300 hover:border-black hover:shadow-lg group"
              >
                <div className="w-12 h-12 bg-black flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Instagram className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-[#666666] uppercase tracking-wider mb-1">Instagram</div>
                  <div className="text-black font-medium">{agencyInfo.instagram}</div>
                </div>
              </a>

              <a
                href={`https://wa.me/${agencyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-6 border border-black/10 transition-all duration-300 hover:border-black hover:shadow-lg group"
              >
                <div className="w-12 h-12 bg-black flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-[#666666] uppercase tracking-wider mb-1">WhatsApp</div>
                  <div className="text-black font-medium">{agencyInfo.whatsapp}</div>
                </div>
              </a>
            </div>

            {/* Response time */}
            <div className="p-6 bg-[#fafafa] border border-black/10">
              <div className="text-black font-semibold mb-2">Quick Response</div>
              <div className="text-sm text-[#666666] font-light">
                We typically respond within 24 hours. For urgent inquiries, WhatsApp is fastest.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;